import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ShareButtons from '../../components/ShareButtons';
import MedicalReviewByline from '../../components/MedicalReviewByline';
import RelatedResources from '../../components/RelatedResources';
import useBlogViews from '../../hooks/useBlogViews';
import { 
  Calendar, 
  Clock, 
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ChevronDown,
  User,
  AlertCircle,
  Baby,
  Eye
} from 'lucide-react';

const BlogArticlePostnatalDiet = () => {
  const [openFaq, setOpenFaq] = React.useState(null);
  const { formattedViews } = useBlogViews('best-diet-plan-after-delivery');
  const articleUrl = 'https://tcubefitness.com/blog/best-diet-plan-after-delivery';
  const articleTitle = 'Best Diet Plan After Delivery: A Complete Postnatal Recovery Guide';

  const faqs = [
    {
      question: "What is the best diet after pregnancy?",
      answer: "The best diet after pregnancy includes high protein, healthy fats, complex carbohydrates, and essential nutrients like iron, calcium, and omega-3. It should focus on recovery, not rapid weight loss."
    },
    {
      question: "How many calories are needed after delivery?",
      answer: "Breastfeeding mothers require approximately 450 to 500 extra calories per day, while non-breastfeeding mothers can follow normal calorie intake."
    },
    {
      question: "When can I start weight loss after pregnancy?",
      answer: "Weight loss should begin gradually after 6 to 8 weeks, once recovery is complete and with medical approval."
    },
    {
      question: "What foods should be avoided after delivery?",
      answer: "Avoid processed foods, excess sugar, deep fried foods, and crash diets as they can slow recovery and affect overall health."
    },
    {
      question: "Is dieting safe during breastfeeding?",
      answer: "Extreme dieting is not recommended during breastfeeding. A balanced diet with adequate calories and nutrients is essential to maintain milk supply."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Diet Plan After Delivery: Complete Postnatal Recovery Guide | TCUBE FITNESS</title>
        <meta name="description" content="Comprehensive postnatal diet plan for Indian mothers. Learn essential nutrients, recovery-focused eating, breastfeeding nutrition, and practical strategies for postpartum health." />
        <meta name="keywords" content="postnatal diet plan, post pregnancy diet, postpartum nutrition, breastfeeding diet India, after delivery diet, postpartum recovery food, new mother diet plan, lactation diet" />
        <link rel="canonical" href="https://tcubefitness.com/blog/best-diet-plan-after-delivery" />
        <meta property="og:title" content="Best Diet Plan After Delivery: Complete Postnatal Recovery Guide" />
        <meta property="og:description" content="Doctor-approved postnatal nutrition guide covering recovery, breastfeeding, and gradual weight loss after pregnancy." />
        <meta property="og:url" content="https://tcubefitness.com/blog/best-diet-plan-after-delivery" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Best Diet Plan After Delivery: A Complete Postnatal Recovery Guide",
            "author": {
              "@type": "Person",
              "name": "Dr. Pooja Sree",
              "jobTitle": "Medical Lead",
              "worksFor": {
                "@type": "Organization",
                "name": "TCUBE FITNESS"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "TCUBE FITNESS",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_8b2c67d7-f3f8-4dd8-ad33-921e8e7f1a96/artifacts/s6b7c1yc_transprant.png"
              }
            },
            "datePublished": "2025-01-15",
            "dateModified": "2025-01-15",
            "description": "Comprehensive postnatal diet plan for recovery, breastfeeding nutrition, and gradual weight loss after pregnancy."
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Article Header */}
        <section className="pt-28 pb-12 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto">
              <Link to="/blog" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
              
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500/10 text-orange-400 text-xs font-medium rounded-full mb-4">
                Postnatal Nutrition
              </span>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Best Diet Plan After Delivery: A Complete Postnatal Recovery Guide
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-slate-400 text-sm mb-4">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Dr. Pooja Sree
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  January 2025
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  12 min read
                </span>
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  {formattedViews} views
                </span>
              </div>
              
              <ShareButtons url={articleUrl} title={articleTitle} />
              <MedicalReviewByline className="mt-4" />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto prose prose-invert prose-lg">
              
              <p className="text-xl text-slate-300 leading-relaxed">
                The postnatal period is one of the most critical phases in a woman's life, yet it is often overlooked when it comes to structured nutrition. What you eat after delivery directly affects your recovery, energy levels, hormonal balance, and, if breastfeeding, your baby's nutrition.
              </p>
              
              <p className="text-slate-300">
                This comprehensive guide covers everything you need to know about a <strong className="text-white">post pregnancy diet plan</strong>, including essential nutrients, recovery-focused eating, and practical strategies.
              </p>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Understanding Postnatal Nutrition Basics</h2>
              
              <p className="text-slate-300">
                A proper <strong className="text-white">postnatal diet plan</strong> is not about rapid weight loss. It is about <strong className="text-orange-400">recovery, nourishment, and gradual return to strength</strong>.
              </p>
              
              <p className="text-slate-300">After delivery, your body needs to:</p>
              
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>Heal tissues (especially after C-section or tearing)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>Restore blood levels</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>Balance hormones</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>Support breast milk production</span>
                </li>
              </ul>
              
              <p className="text-slate-300 mt-4">
                The focus should be on <strong className="text-white">nutrient-dense foods</strong>, not calorie restriction.
              </p>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Early Postpartum Nutrition (Weeks 1-6)</h2>
              
              <p className="text-slate-300">
                The first six weeks are focused on healing and recovery.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Key Nutrients for Early Recovery</h3>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800 my-6">
                <h4 className="text-lg font-semibold text-white mb-4">Daily Nutritional Requirements</h4>
                <ul className="space-y-4 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-white">Protein (75-90g daily):</strong> Supports tissue repair. Include dal, paneer, eggs, chicken, fish, and legumes.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-white">Iron:</strong> Helps recover blood loss. Include green leafy vegetables, dates, jaggery, and iron-fortified foods.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-white">Fluids (3-3.5 liters daily):</strong> Supports hydration and milk production.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-white">Healthy Fats:</strong> Support hormonal balance. Include ghee, nuts, seeds, and coconut.
                    </div>
                  </li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Managing Early Postpartum Challenges</h3>
              
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Eat small, frequent meals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Choose easy-to-digest foods</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Stay hydrated</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Avoid skipping meals</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Mid Postpartum Nutrition (Weeks 6-12)</h2>
              
              <p className="text-slate-300">
                As recovery improves, focus shifts to rebuilding strength.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Key Nutrients</h3>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800 my-6">
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div><strong className="text-white">Protein (80-100g daily)</strong></div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div><strong className="text-white">Calcium (1000 mg daily)</strong> - Milk, curd, paneer, ragi</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div><strong className="text-white">Vitamin D</strong> - Sunlight exposure, fortified foods</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div><strong className="text-white">Vitamin B12</strong> - Eggs, dairy, fortified cereals</div>
                  </li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Nutritional Focus</h3>
              
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Balanced meals with all food groups</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Consistent meal timing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Gradual fat loss (if required)</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Late Postpartum Nutrition (3 Months and Beyond)</h2>
              
              <p className="text-slate-300">
                Focus on long-term health and sustainable weight management.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Key Nutrients</h3>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800 my-6">
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-pink-400 mt-0.5 flex-shrink-0" />
                    <div><strong className="text-white">Omega-3 Fatty Acids (DHA)</strong> - Fish, walnuts, flaxseeds</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-pink-400 mt-0.5 flex-shrink-0" />
                    <div><strong className="text-white">Fiber</strong> - Whole grains, vegetables, fruits</div>
                  </li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Focus Areas</h3>
              
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Structured fat loss approach</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Balanced macronutrients</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Regular exercise after medical clearance</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Foods to Avoid After Delivery</h2>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-red-500/20 my-6">
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold text-lg">×</span>
                    <span>Processed and junk foods</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold text-lg">×</span>
                    <span>Excess sugar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold text-lg">×</span>
                    <span>Deep fried foods</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold text-lg">×</span>
                    <span>Crash diets</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 font-bold text-lg">×</span>
                    <span>Excess caffeine</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Key ACOG-Based Postnatal Nutrition Guidelines</h2>

              <div className="bg-gradient-to-r from-orange-900/30 to-amber-900/30 rounded-xl p-6 border border-orange-500/20 my-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Baby className="w-5 h-5 text-orange-400" />
                  Daily Nutritional Requirements
                </h4>
                <div className="space-y-4 text-slate-300">
                  <div className="border-b border-slate-700 pb-3">
                    <p className="font-semibold text-white">Extra Calories</p>
                    <p>Breastfeeding mothers: approximately 450 to 500 kcal per day</p>
                    <p>Non-breastfeeding mothers: normal calorie intake</p>
                  </div>
                  <div className="border-b border-slate-700 pb-3">
                    <p className="font-semibold text-white">Protein</p>
                    <p>75 to 100 grams per day</p>
                  </div>
                  <div className="border-b border-slate-700 pb-3">
                    <p className="font-semibold text-white">Calcium</p>
                    <p>1000 mg per day</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Fluids</p>
                    <p>3 to 3.5 liters per day</p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Key Recommendations</h3>
              
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>Eat balanced meals with protein, carbs, and healthy fats</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>Prefer small and frequent meals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>Focus on recovery before weight loss</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Special Dietary Considerations</h2>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Breastfeeding Mothers</h3>
              
              <p className="text-slate-300">
                A proper <strong className="text-white">breastfeeding diet plan</strong> should include:
              </p>
              
              <ul className="space-y-2 text-slate-300 mt-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>Adequate calorie intake</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>High protein</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>Proper hydration</span>
                </li>
              </ul>
              
              <p className="text-slate-300 mt-4">
                Avoid extreme dieting, as it may affect milk production.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Post Pregnancy Weight Loss</h3>
              
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Start after 6-8 weeks</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Avoid crash dieting</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Focus on gradual fat loss</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">When to Seek Professional Guidance</h2>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-500/20 my-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                  Consult a Professional If You Experience
                </h4>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400">•</span>
                    <span>Low milk supply</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400">•</span>
                    <span>Severe fatigue</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400">•</span>
                    <span>Nutrient deficiencies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400">•</span>
                    <span>Difficulty losing weight</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400">•</span>
                    <span>Medical conditions</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 rounded-xl p-6 border border-emerald-500/20 my-8">
                <h4 className="text-lg font-semibold text-white mb-3">Get Personalized Postnatal Nutrition Support</h4>
                <p className="text-slate-300 mb-4">
                  Every mother's recovery journey is unique. Our <Link to="/pregnancy-nutrition" className="text-emerald-400 hover:underline">gynecologist-supervised nutrition program</Link> creates personalized postpartum plans based on your specific circumstances, delivery type, and health goals.
                </p>
                <Link 
                  to="/submit-details"
                  className="inline-flex items-center gap-2 text-emerald-400 font-medium hover:text-emerald-300"
                >
                  Book a Free Consultation
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Disclaimer */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 my-8">
                <p className="text-slate-400 text-sm">
                  <strong className="text-slate-300">Disclaimer:</strong> This content is for informational purposes only and should not replace professional medical advice. Always consult a qualified healthcare provider for personalized guidance.
                </p>
              </div>

            </div>

            {/* FAQ Section */}
            <div className="max-w-3xl mx-auto mt-16">
              <h2 className="text-2xl font-bold text-white mb-8">Frequently Asked Questions About Postnatal Nutrition</h2>
              
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <span className="text-white font-semibold pr-4">{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-orange-400' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                      <p className="px-5 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Articles */}
            <div className="max-w-3xl mx-auto mt-16">
              <h3 className="text-xl font-bold text-white mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Link to="/blog/best-diet-plan-for-pregnancy" className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 hover:border-emerald-500/30 transition-colors">
                  <span className="text-emerald-400 text-sm font-medium">Pregnancy Nutrition</span>
                  <p className="text-white font-medium mt-1">Best Diet Plan for Pregnancy: Complete Guide</p>
                </Link>
                <Link to="/blog/pcos-diet-plan-complete-guide" className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 hover:border-blue-500/30 transition-colors">
                  <span className="text-blue-400 text-sm font-medium">Women's Health</span>
                  <p className="text-white font-medium mt-1">PMOS (previously called PCOS) Diet Plan: Complete Management Guide</p>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <div className="container mx-auto px-4 sm:px-6 pb-20">
          <div className="max-w-3xl mx-auto">
            <RelatedResources slug="best-diet-plan-after-delivery" />
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default BlogArticlePostnatalDiet;
